import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { NotificationType } from "shared/utils/enum";
import { getSocket } from "shared/services/socket";
import {
  GetNotificationsService,
  MarkAllNotificationsReadService,
  ReadNotificationService,
} from "shared/services/notification";
import {
  handleErrors,
  formatTimestamp,
  groupNotificationsByDate,
  GroupedNotificationItem,
} from "shared/utils/helper";
import { Icons } from "assets";
import * as io from "socket.io-client";
import styles from "./style.module.scss";
import CustomPageHeading from "shared/components/common/customPageHeading";
import CustomNotifications from "shared/components/common/customNotificationCard";
import classNames from "classnames";
import CustomDate from "shared/components/common/customDateComponent";
import NotificationsListSkeleton from "shared/components/common/notificationsListSkeleton";
import Spinner from "shared/components/common/spinner";
import NoContentCard from "shared/components/common/noContentCard";
import { resolveNotificationTarget } from "shared/utils/notificationNavigation";
import {
  resolveNotificationPresentation,
  NotificationTone,
} from "shared/utils/notificationPresentation";

interface NotificationItem {
  id: number;
  icon?: React.ReactNode;
  title: string;
  description?: string;
  author?: string;
  timeAgo?: string;
  unread: boolean;
  showBorder: boolean;
  created_at: string;
  tone: NotificationTone;
  fullData?: any;
}

interface NotificationsListProps {
  title?: string;
  description?: string;
  pageHeadingSize?: "small" | "large";
  customPageWrapperClassName?: string;
  customHeadingWrapperClassName?: string;
}

const NotificationsList = ({
  title = "Notifications",
  description = "Stay updated on project activity and feedback.",
  pageHeadingSize,
  customPageWrapperClassName,
  customHeadingWrapperClassName,
}: NotificationsListProps) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [limit] = useState(10);
  const [unreadTotal, setUnreadTotal] = useState<number>(0);
  const [markAllLoading, setMarkAllLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasInitialFetch = useRef(false);
  const isFetchingRef = useRef(false);
  const hasAutoMarkedReadRef = useRef(false);
  const currentPageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector((state: any) => state.root.auth);
  const userId = auth?.user?.user_id || auth?.user?.id;
  const socketRef = useRef<io.Socket | null>(null);

  const getNotificationIcon = (type?: string) => {
    switch (type?.toLowerCase()) {
      case "project_created":
        return <Icons.ProjectCreatedIcon width={44} height={44} />;
      case "project_ongoing":
      case "revision_submitted":
        return <Icons.OnGoingState width={44} height={44} />;
      case "revision_approved":
        return <Icons.RevisionUploadedIcon width={44} height={44} />;
      case "comment_added":
      case "new_comment":
        return <Icons.NewCommentIcon width={44} height={44} />;
      case "reply_added":
      case "new_reply":
        return <Icons.NewReplyIcon width={44} height={44} />;
      case "project_completed":
        return <Icons.ProjectCompletedIcon width={44} height={44} />;
      case "project_onboarded":
        return <Icons.ProjectOnboardedIcon width={44} height={44} />;
      case "project_status_changed":
        return <Icons.ProjectStatusChangedIcon width={44} height={44} />;
      case "client_responded":
        return <Icons.ClientRespondedIcon width={44} height={44} />;
      case NotificationType.PROJECT_DESIGNER_ADDED:
        return <Icons.AddUsersIcon width={44} height={44} />;
      default:
        return <Icons.UnRead width={44} height={44} />;
    }
  };

  const toneClass = (tone: NotificationTone) =>
    tone === "default" ? undefined : styles[`tone${tone}`];


  const fetchNotifications = useCallback(
    async (pageNum: number, append: boolean = false) => {
      if (!append && hasInitialFetch.current) return;
      if (append && (loadingMoreRef.current || isFetchingRef.current)) return;

      isFetchingRef.current = true;
      try {
        if (append) {
          setLoadingMore(true);
          loadingMoreRef.current = true;
        } else {
          setLoading(true);
          hasInitialFetch.current = true;
        }

        const response = await GetNotificationsService(pageNum, limit);
        const data = response.data;

        const pathname = location.pathname;
        const isClientOrCollaboratorNotifications =
          pathname.includes("/client/notifications") ||
          pathname.includes("/collaborator/notifications");

        const currentUserIdStr = userId?.toString();
        const rawItems = data?.data || [];

        const filteredItems = isClientOrCollaboratorNotifications
          ? rawItems
          : rawItems.filter((item: any) => {
            const generatorUserId =
              item.generator?.user_id?.toString() ||
              item.generator?.id?.toString() ||
              item.generator?.userId?.toString();

            const notificationType = item.type?.toLowerCase();
            const isChatNotification =
              notificationType === "comment_added" ||
              notificationType === "new_comment" ||
              notificationType === "reply_added" ||
              notificationType === "new_reply";

            if (isChatNotification) {
              const commentUserId =
                item.comment?.user_id?.toString() ||
                item.comment?.user?.user_id?.toString() ||
                item.comment?.user?.id?.toString() ||
                item.comment?.userId?.toString() ||
                item.data?.comment?.user_id?.toString() ||
                item.data?.comment?.user?.user_id?.toString() ||
                item.data?.comment?.user?.id?.toString() ||
                item.metadata?.comment?.user_id?.toString() ||
                item.metadata?.comment?.user?.user_id?.toString();

              const replyUserId =
                item.reply?.user_id?.toString() ||
                item.reply?.user?.user_id?.toString() ||
                item.reply?.user?.id?.toString() ||
                item.reply?.userId?.toString() ||
                item.data?.reply?.user_id?.toString() ||
                item.data?.reply?.user?.user_id?.toString() ||
                item.data?.reply?.user?.id?.toString() ||
                item.metadata?.reply?.user_id?.toString() ||
                item.metadata?.reply?.user?.user_id?.toString();

              return !(
                generatorUserId === currentUserIdStr ||
                commentUserId === currentUserIdStr ||
                replyUserId === currentUserIdStr
              );
            } else {
              return generatorUserId !== currentUserIdStr;
            }
          });

        const transformedNotifications: NotificationItem[] = filteredItems.map(
          (item: any) => ({
            id: item.notification_id,
            icon: getNotificationIcon(item.type),
            title: resolveNotificationPresentation(item.type, item.title).title,
            tone: resolveNotificationPresentation(item.type, item.title).tone,
            description: item.message,
            author: item.generator?.display_name || item.project?.name,
            timeAgo: formatTimestamp(item.created_at),
            unread: !item.is_read,
            showBorder: !item.is_read,
            created_at: item.created_at,
            fullData: item,
          }),
        );

        if (append) {
          setNotifications((prev) => {
            const existingIds = new Set(prev.map((n) => n.id));
            const newNotifications = transformedNotifications.filter(
              (n) => !existingIds.has(n.id),
            );
            return [...prev, ...newNotifications];
          });
        } else {
          setNotifications(transformedNotifications);
        }

        const meta = data?.meta || {};
        const totalPages = meta.total_pages || 1;
        const hasMoreData = pageNum < totalPages;
        setHasMore(hasMoreData);
        hasMoreRef.current = hasMoreData;

        if (!append || pageNum === 1) {
          const apiUnreadTotal = meta.unread_total ?? 0;
          setUnreadTotal(apiUnreadTotal);
        }

        if (append) {
          setPage(pageNum);
          currentPageRef.current = pageNum;
        } else {
          currentPageRef.current = 1;
        }
      } catch (error) {
        handleErrors(error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
        isFetchingRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [limit, location.pathname],
  );

  useEffect(() => {
    if (!hasInitialFetch.current) {
      currentPageRef.current = 1;
      hasMoreRef.current = true;
      loadingMoreRef.current = false;
      fetchNotifications(1, false);
    }
    // eslint-disable-next-line
  }, []);

  // Auto-clear the dashboard notification bubble as soon as the user views
  // their notifications: mark everything read on the backend and reset the
  // badge count, while keeping the unread highlighting visible for this view.
  useEffect(() => {
    if (loading || hasAutoMarkedReadRef.current || unreadTotal <= 0) return;
    hasAutoMarkedReadRef.current = true;
    MarkAllNotificationsReadService()
      .then(() => {
        setUnreadTotal(0);
        window.dispatchEvent(
          new CustomEvent("notification:sync-count", {
            detail: { count: 0 },
          }),
        );
        window.dispatchEvent(new Event("notification:refresh-count"));
      })
      .catch(() => {
        hasAutoMarkedReadRef.current = false;
      });
  }, [loading, unreadTotal]);

  useEffect(() => {
    if (!userId) return;

    const socket = getSocket();

    socketRef.current = socket;

    if (!socket) return;

    const handleNewNotification = (data: any) => {
      const currentUserIdStr = userId?.toString();
      const generatorUserId =
        data.generator?.user_id?.toString() ||
        data.generator?.id?.toString() ||
        data.generator?.userId?.toString();

      const notificationType = data.type?.toLowerCase();
      const isChatNotification =
        notificationType === "comment_added" ||
        notificationType === "new_comment" ||
        notificationType === "reply_added" ||
        notificationType === "new_reply";

      let isFromCurrentUser = false;

      if (isChatNotification) {
        const commentUserId =
          data.comment?.user_id?.toString() ||
          data.comment?.user?.user_id?.toString() ||
          data.comment?.user?.id?.toString() ||
          data.comment?.userId?.toString() ||
          data.data?.comment?.user_id?.toString() ||
          data.data?.comment?.user?.user_id?.toString() ||
          data.data?.comment?.user?.id?.toString() ||
          data.metadata?.comment?.user_id?.toString() ||
          data.metadata?.comment?.user?.user_id?.toString();

        const replyUserId =
          data.reply?.user_id?.toString() ||
          data.reply?.user?.user_id?.toString() ||
          data.reply?.user?.id?.toString() ||
          data.reply?.userId?.toString() ||
          data.data?.reply?.user_id?.toString() ||
          data.data?.reply?.user?.user_id?.toString() ||
          data.data?.reply?.user?.id?.toString() ||
          data.metadata?.reply?.user_id?.toString() ||
          data.metadata?.reply?.user?.user_id?.toString();

        isFromCurrentUser =
          generatorUserId === currentUserIdStr ||
          commentUserId === currentUserIdStr ||
          replyUserId === currentUserIdStr;
      } else {
        isFromCurrentUser = generatorUserId === currentUserIdStr;
      }

      if (isFromCurrentUser) {
        return;
      }

      const newNotification: NotificationItem = {
        id: data.notification_id || data.id,
        icon: getNotificationIcon(data.type),
        title: resolveNotificationPresentation(data.type, data.title).title,
        tone: resolveNotificationPresentation(data.type, data.title).tone,
        description: data.message || data.description,
        author: data.generator?.display_name || data.project?.name,
        timeAgo: formatTimestamp(data.created_at || new Date().toISOString()),
        unread: true,
        showBorder: true,
        created_at: data.created_at || new Date().toISOString(),
        fullData: data,
      };

      setNotifications((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        if (existingIds.has(newNotification.id)) {
          return prev;
        }
        return [newNotification, ...prev];
      });

      setUnreadTotal((prevTotal) => {
        const newTotal = prevTotal + 1;
        window.dispatchEvent(
          new CustomEvent("notification:sync-count", {
            detail: { count: newTotal },
          }),
        );
        return newTotal;
      });
    };

    const setupListener = () => {
      const currentSocket = socketRef.current;
      if (currentSocket && currentSocket.connected) {
        currentSocket.off("notification", handleNewNotification);
        currentSocket.on("notification", handleNewNotification);
      }
    };

    if (socket.connected) {
      setupListener();
    } else {
      const connectHandler = () => {
        setupListener();
      };
      socket.once("connect", connectHandler);
    }

    const reconnectHandler = () => {
      setupListener();
    };
    socket.on("reconnect", reconnectHandler);

    return () => {
      const currentSocket = socketRef.current;
      if (currentSocket) {
        currentSocket.off("notification", handleNewNotification);
        currentSocket.off("reconnect", reconnectHandler);
      }
    };
  }, [userId]);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    if (
      loadingMoreRef.current ||
      !hasMoreRef.current ||
      isFetchingRef.current
    ) {
      return;
    }

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    const threshold = 150;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

    if (distanceFromBottom <= threshold) {
      const nextPage = currentPageRef.current + 1;
      fetchNotifications(nextPage, true);
    }
  }, [fetchNotifications]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollHandler = () => {
      handleScroll();
    };

    container.addEventListener("scroll", scrollHandler, { passive: true });

    const checkInitialLoad = () => {
      if (!loading && !loadingMore && hasMore && notifications.length > 0) {
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;

        if (scrollHeight <= clientHeight + 50 && !isFetchingRef.current) {
          const nextPage = currentPageRef.current + 1;
          fetchNotifications(nextPage, true);
        }
      }
    };

    const checkTimeout = setTimeout(checkInitialLoad, 100);

    return () => {
      clearTimeout(checkTimeout);
      container.removeEventListener("scroll", scrollHandler);
    };
  }, [
    handleScroll,
    loading,
    loadingMore,
    hasMore,
    notifications.length,
    fetchNotifications,
  ]);

  const handleNotificationClick = useCallback(
    async (notification: NotificationItem) => {
      try {
        if (notification.unread) {
          await ReadNotificationService(notification.id);
          setNotifications((prev) =>
            prev.map((n) =>
              n.id === notification.id
                ? { ...n, unread: false, showBorder: false }
                : n,
            ),
          );
          setUnreadTotal((prevTotal) => {
            const newTotal = Math.max(0, prevTotal - 1);
            window.dispatchEvent(
              new CustomEvent("notification:sync-count", {
                detail: { count: newTotal },
              }),
            );
            return newTotal;
          });
          window.dispatchEvent(new Event("notification:refresh-count"));
        }

        const fullData = notification.fullData;
        if (!fullData) return;

        const target = resolveNotificationTarget(fullData, location.pathname);
        if (target) {
          navigate(target.path, { state: target.state });
        }
      } catch (error) {
        handleErrors(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, location.pathname],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMarkAllRead = useCallback(async () => {
    try {
      setMarkAllLoading(true);
      const response = await MarkAllNotificationsReadService();
      if (response.status === 200 || response.status === 201) {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, unread: false, showBorder: false })),
        );
        setUnreadTotal(0);
        window.dispatchEvent(
          new CustomEvent("notification:sync-count", { detail: { count: 0 } }),
        );
        window.dispatchEvent(new Event("notification:refresh-count"));
      } else {
        handleErrors(response);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setMarkAllLoading(false);
    }
  }, []);

  const unReadNot = notifications.filter((n) => n.unread);
  const readNot = notifications.filter((n) => !n.unread);
  const groupedUnread = groupNotificationsByDate(
    unReadNot,
  ) as GroupedNotificationItem[];
  const groupedRead = groupNotificationsByDate(
    readNot,
  ) as GroupedNotificationItem[];

  useEffect(() => {
    if (!loading) {
      window.dispatchEvent(
        new CustomEvent("notification:sync-count", {
          detail: { count: unreadTotal },
        }),
      );
    }
  }, [unreadTotal, loading]);

  const headingContent = (
    <CustomPageHeading
      title={title}
      size={pageHeadingSize}
      description={description}
    />
  );

  if (loading && notifications.length === 0) {
    return (
      <div className={classNames(customPageWrapperClassName)}>
        <NotificationsListSkeleton unreadCount={2} readCount={3} />
      </div>
    );
  }

  return (
    <div
      className={classNames(
        styles.pageWrapper,
        customPageWrapperClassName,
        "flex flex-col h-full",
      )}
    >
      {customHeadingWrapperClassName ? (
        <div className={customHeadingWrapperClassName}>{headingContent}</div>
      ) : (
        headingContent
      )}

      {notifications.length === 0 ? (
        <div className="flex items-center justify-center h-[700px]">
          <NoContentCard
            label1="You’re all set — no notifications yet"
            label2=""
            Icon={Icons.NoNotificationsIcon}
          />
        </div>
      ) : (
        <div
          ref={containerRef}
          className={classNames(styles.notificationsList, "grid gap-3")}
        >
          {groupedUnread.map((group, groupIndex) => (
            <div key={`unread-${groupIndex}`}>
              <div
                className={classNames(
                  styles.dateRowHeader,
                  "flex items-center justify-between mb-3",
                )}
              >
                <div className="flex items-center">
                  <CustomDate
                    icon={<Icons.CalendarIcon />}
                    keyword={group.keyword}
                    date={group.dateFormatted}
                    year={group.year}
                  />
                  {groupIndex === 0 && unreadTotal > 0 && (
                    <>
                      <span className={styles.dotSeparator}>•</span>
                      <span className={styles.unreadCountText}>
                        {unreadTotal} unread notifications
                      </span>
                    </>
                  )}
                </div>
                {groupIndex === 0 && unreadTotal > 0 && (
                  <button
                    type="button"
                    className={styles.readAllButton}
                    onClick={handleMarkAllRead}
                    disabled={markAllLoading}
                  >
                    {markAllLoading ? "..." : "Read all"}
                  </button>
                )}
              </div>
              <div className={classNames(styles.unreadNotifications)}>
                {group.notifications.map((n: NotificationItem) => (
                  <CustomNotifications
                    key={n.id}
                    icon={n.icon}
                    title={n.title}
                    description={n.description}
                    author={n.fullData?.project?.name}
                    timeAgo={n.timeAgo}
                    unread={n.unread}
                    className={toneClass(n.tone)}
                    showBorder={n.showBorder}
                    onClick={() => handleNotificationClick(n)}
                  />
                ))}
              </div>
            </div>
          ))}

          {groupedRead.map((group, groupIndex) => (
            <div
              key={`read-${groupIndex}`}
              className={classNames(styles.readEnclosure, "grid")}
            >
              <CustomDate
                icon={<Icons.CalendarIcon />}
                keyword={group.keyword}
                date={group.dateFormatted}
                year={group.year}
              />
              <div className={classNames(styles.notificationsEnclosure)}>
                {group.notifications.map((n: NotificationItem) => (
                  <CustomNotifications
                    key={n.id}
                    icon={n.icon}
                    title={n.title}
                    description={n.description}
                    author={n.fullData?.project?.name}
                    timeAgo={n.timeAgo}
                    unread={n.unread}
                    className={classNames(styles.readNotifications, toneClass(n.tone))}
                    showBorder={n.showBorder}
                    onClick={() => handleNotificationClick(n)}
                  />
                ))}
              </div>
            </div>
          ))}

          {loadingMore && hasMore && (
            <div
              className="flex flex-col items-center justify-center w-full"
              style={{
                paddingTop: "32px",
                paddingBottom: "32px",
                minHeight: "100px",
                width: "100%",
              }}
            >
              <Spinner />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsList;
